import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  GenealogyTree,
  GenealogyTreeSkeleton,
  Input,
  Money,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  type AddSlotContext,
  type GenealogyTreeNode,
} from '@mlm/ui';
import { Loader2, Search, X } from 'lucide-react';
import {
  GENEALOGY_MAX_DEPTH,
  currencyWord,
  labelizeLedgerEntry,
  labelizeLedgerKind,
  labelizeSourcePlan,
  membershipHierarchy,
  slotStatusLabel,
  sortMembershipsByHierarchy,
} from '@mlm/shared';

// Depth bumps clamp to the API's cap so expansion at the boundary stops
// loading instead of tripping the endpoint's zod max into a 400.
const clampDepth = (d: number) => Math.min(d, GENEALOGY_MAX_DEPTH);
import {
  adjustSlotDebt,
  adminChangeSlotOwner,
  adminGetGenealogy,
  adminGetSlot,
  adminGetUpgradeHistory,
  adminPreviewSponsorTransfer,
  adminRefireUpgradeBonus,
  adminTransferSponsor,
  adminUpgradeSlot,
  adminSearchGenealogy,
  api,
  getSlotDebt,
  getTenantFeatures,
  listMemberships,
  type AdminGenealogySearchMatch,
  type AdminSlotDetail,
  type AdminUpgradeHistoryRow,
  type AdminUserSearchResult,
  type SponsorTransferPreview,
} from '../lib/api';
import { MemberPicker } from '../components/member-picker';
import { SlotCodePicker } from '../components/slot-code-picker';
import { CreateSlotDialog } from '../components/slots/create-slot-dialog';
import { PlaceSlotDialog } from '../components/slots/place-slot-dialog';
import { AdjustDialog } from '../components/adjust-dialog';
import { useHasPerm, useIsSuperAdmin } from '../lib/auth-store';

/** Shared ledger display-name labels (admin overrides + built-in defaults). */
function useLedgerLabels() {
  const { data } = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  return {
    kindLabels: data?.ledger_labels,
    planLabels: data?.ledger_source_plan_labels,
    coinLabel: data?.coin_label ?? 'Coins',
  };
}

type LedgerRow = {
  id: number | string;
  slotId: string;
  userId: string;
  kind: string;
  level: number | null;
  amount: string;
  currency: string;
  currencyKind: 'cash' | 'gc' | 'coin' | 'token';
  direction: 'C' | 'D';
  sourceEventId: string;
  sourcePlan: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  fromSlotId: string | null;
  fromSlotCode: string | null;
  fromOwnerName: string | null;
  triggerSlotId: string | null;
  triggerSlotCode: string | null;
  triggerOwnerName: string | null;
  orderCode: string | null;
  buyerName: string | null;
};

/**
 * After a slot is created or placed under this view, refetch every query
 * that could reflect downstream comp-plan effects: the slot detail (wallet +
 * breakdown + binaryHistory + pvByPeriod), the ledger, the distributions, and
 * the genealogy tree. The wallet materializer runs async in the worker, so
 * we also fire a delayed second invalidate to catch the slot_wallets row
 * update after the ledger insert has been drained (~hundreds of ms).
 */
function refreshAfterPlacement(qc: ReturnType<typeof useQueryClient>) {
  const invalidate = () => {
    qc.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey[0];
        return typeof k === 'string' && k.startsWith('admin-slot');
      },
    });
  };
  // Immediate refresh for the genealogy + structural views.
  invalidate();
  // Second pass once the materializer has had time to drain the new ledger
  // entries into slot_wallets.
  setTimeout(invalidate, 1200);
}

/**
 * Renders the "Triggered by" cell across Wallet Logs and Binary point log.
 * If the slot still exists (slotCode resolved), renders a clickable link
 * showing both the slot code and the owner's name. If the slot was deleted
 * since the log row was written, show "(deleted)" instead of a broken link.
 */
function TriggerSlotCell({
  slotId,
  slotCode,
  ownerName,
}: {
  slotId: string | null;
  slotCode: string | null;
  ownerName?: string | null;
}) {
  if (!slotId) return <span className="text-muted-foreground">none</span>;
  if (!slotCode) {
    return (
      <span className="text-muted-foreground italic" title={slotId}>
        (deleted)
      </span>
    );
  }
  return (
    <Link to={`/slots/${slotId}`} className="text-foreground hover:underline">
      <span className="font-mono">{slotCode}</span>
      {ownerName ? <span className="text-muted-foreground"> · {ownerName}</span> : null}
    </Link>
  );
}

export function SlotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  // Tab is driven by `?tab=` so deep links (e.g. the Slots list wallet figures)
  // can open straight to Wallet + Breakdown or Wallet Logs.
  const [searchParams, setSearchParams] = useSearchParams();
  const canAdjust = useHasPerm('member.balance.adjust');
  // Cross-cutting income-visibility lock — the SAME permission that hides the
  // wallet/earned columns in the Slots list. Hides the money figures on the
  // Information tab and gates the Wallet + Breakdown tab, without hiding the
  // Information tab itself. (super_admin bypasses useHasPerm, so it stays on.)
  const canSeeIncome = useHasPerm('member.income.view');
  // Per-tab locks. Wallet Logs (member.wallet.read) + Distributed Income
  // (distribution.read) hit their own endpoints; the rest gate tabs over the
  // shared GET /admin/slots/:id payload (presentation locks, frontend-enforced).
  const canViewInfo = useHasPerm('slot.detail.info.read');
  const canViewWalletSummary = useHasPerm('slot.detail.wallet.read') && canSeeIncome;
  const canViewWallet = useHasPerm('member.wallet.read');
  const canViewDistribution = useHasPerm('distribution.read');
  const canViewUnilevel = useHasPerm('slot.detail.unilevel.read');
  const canViewBinary = useHasPerm('slot.detail.binary.read');
  const canViewPoints = useHasPerm('slot.detail.points.read');
  const canViewInventory = useHasPerm('slot.detail.inventory.read');
  const canViewBinaryHistory = useHasPerm('slot.detail.binary_history.read');
  const canEditSlot = useHasPerm('slot.write');
  // The admin upgrade override fires live comp on free->paid with no purchase
  // behind it — super_admin only (enforced server-side too).
  const canUpgrade = useIsSuperAdmin();

  // Ordered tab list with its lock; the active tab is the requested `?tab=` when
  // allowed, else the first allowed tab — so locking a tab never strands the page.
  const tabOrder: Array<{ value: string; allowed: boolean }> = [
    { value: 'information', allowed: canViewInfo },
    { value: 'wallet', allowed: canViewWalletSummary },
    { value: 'logs', allowed: canViewWallet },
    { value: 'distributed', allowed: canViewDistribution },
    { value: 'unilevel', allowed: canViewUnilevel },
    { value: 'binary', allowed: canViewBinary },
    { value: 'points', allowed: canViewPoints },
    { value: 'inventory', allowed: canViewInventory },
    { value: 'binary-history', allowed: canViewBinaryHistory },
  ];
  const allowedTabs = tabOrder.filter((t) => t.allowed).map((t) => t.value);
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && allowedTabs.includes(requestedTab)
      ? requestedTab
      : (allowedTabs[0] ?? 'information');

  const slotQ = useQuery({
    queryKey: ['admin-slot', id],
    queryFn: () => adminGetSlot(id!),
    enabled: !!id,
  });

  const ledgerQ = useQuery({
    queryKey: ['admin-slot-ledger', id],
    queryFn: async () =>
      (
        await api.get<LedgerRow[]>(`/wallet/${slotQ.data!.userId}/ledger`, {
          params: { slotId: id, pageSize: 100 },
        })
      ).data,
    enabled: !!slotQ.data?.userId,
  });

  if (slotQ.isLoading) {
    return <Skeleton className="h-64" />;
  }
  if (slotQ.error || !slotQ.data) {
    return <p className="text-sm text-destructive">Slot not found.</p>;
  }
  const s = slotQ.data;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/slots" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to slots
        </Link>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <span className="font-mono">{s.slotCode}</span>
            <Badge variant={s.status === 'active' ? 'default' : 'secondary'}>
              {slotStatusLabel(s.status)}
            </Badge>
            {s.isCompany && <Badge variant="outline">Company</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Owner:{' '}
            <Link to={`/members/${s.userId}`} className="text-foreground hover:underline">
              {s.ownerFullName ?? s.ownerEmail}
            </Link>{' '}
            · {s.membershipName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canAdjust && (
            <>
              <AdjustDialog kind="wallet" userId={s.userId} slotId={s.id} slotCode={s.slotCode} />
              <AdjustDialog kind="points" userId={s.userId} slotId={s.id} slotCode={s.slotCode} />
            </>
          )}
          {canUpgrade && <UpgradeSlotDialog s={s} qc={qc} />}
          {canEditSlot && (
            <>
              <CompanyToggle slotId={s.id} isCompany={s.isCompany} qc={qc} />
              <ChangeOwnerDialog
                slotId={s.id}
                currentOwnerLabel={s.ownerFullName ?? s.ownerEmail}
              />
              <TransferSponsorDialog
                slotId={s.id}
                slotCode={s.slotCode}
                currentSponsorCode={s.sponsor?.slotCode ?? null}
                isPlaced={s.binary !== null}
              />
            </>
          )}
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setSearchParams(
            (prev) => {
              prev.set('tab', v);
              return prev;
            },
            { replace: true },
          )
        }
      >
        <TabsList className="flex flex-wrap h-auto">
          {canViewInfo && <TabsTrigger value="information">Information</TabsTrigger>}
          {canViewWalletSummary && <TabsTrigger value="wallet">Wallet + Breakdown</TabsTrigger>}
          {canViewWallet && <TabsTrigger value="logs">Wallet Logs</TabsTrigger>}
          {canViewDistribution && <TabsTrigger value="distributed">Distributed Income</TabsTrigger>}
          {canViewUnilevel && <TabsTrigger value="unilevel">Unilevel Tree</TabsTrigger>}
          {canViewBinary && <TabsTrigger value="binary">Binary Tree</TabsTrigger>}
          {canViewPoints && <TabsTrigger value="points">Points History</TabsTrigger>}
          {canViewInventory && <TabsTrigger value="inventory">Inventory</TabsTrigger>}
          {canViewBinaryHistory && <TabsTrigger value="binary-history">Binary History</TabsTrigger>}
        </TabsList>

        {canViewInfo && (
          <TabsContent value="information">
            <div className="space-y-4">
              <InfoTab s={s} canSeeIncome={canSeeIncome} />
              {canAdjust && <CdDebtCard slotId={s.id} />}
              {canUpgrade && <UpgradeHistoryCard slotId={s.id} qc={qc} />}
            </div>
          </TabsContent>
        )}
        {canViewWalletSummary && (
          <TabsContent value="wallet">
            <WalletTab s={s} />
          </TabsContent>
        )}
        {canViewWallet && (
          <TabsContent value="logs">
            <LedgerTab rows={ledgerQ.data} loading={ledgerQ.isLoading} />
          </TabsContent>
        )}
        {canViewDistribution && (
          <TabsContent value="distributed">
            <DistributedTab slotId={id!} />
          </TabsContent>
        )}
        {canViewUnilevel && (
          <TabsContent value="unilevel">
            <GenealogyTab slotId={id!} view="unilevel" />
          </TabsContent>
        )}
        {canViewBinary && (
          <TabsContent value="binary">
            <GenealogyTab slotId={id!} view="binary" />
          </TabsContent>
        )}
        {canViewPoints && (
          <TabsContent value="points">
            <PointsHistoryTab slotId={id!} />
          </TabsContent>
        )}
        {canViewInventory && (
          <TabsContent value="inventory">
            <EmptyTab message="Per-slot inventory tracking ships when stockist inventory becomes a real product concept. Today inventory lives on branches." />
          </TabsContent>
        )}
        {canViewBinaryHistory && (
          <TabsContent value="binary-history">
            <BinaryHistoryTab slotId={id!} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function InfoTab({ s, canSeeIncome }: { s: AdminSlotDetail; canSeeIncome: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Information</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Slot code</dt>
          <dd className="font-mono">{s.slotCode}</dd>
          <dt className="text-muted-foreground">Status</dt>
          <dd>{s.status}</dd>
          <dt className="text-muted-foreground">Membership</dt>
          <dd>
            {s.membershipName} <span className="text-muted-foreground">({s.membershipCode})</span>
          </dd>
          {s.stairstepActive && (
            <>
              <dt className="text-muted-foreground">Rank</dt>
              <dd>
                {s.currentRank ? (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 capitalize">
                    {s.currentRank}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Unranked</span>
                )}
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">Owner</dt>
          <dd>
            <Link to={`/members/${s.userId}`} className="hover:underline">
              {s.ownerFullName ?? s.ownerEmail}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd>{s.ownerEmail}</dd>
          <dt className="text-muted-foreground">Username</dt>
          <dd>{s.ownerUsername ?? '—'}</dd>
          <dt className="text-muted-foreground">Contact</dt>
          <dd>{s.ownerPhone ?? '—'}</dd>
          <dt className="text-muted-foreground">Address</dt>
          <dd>{formatAddress(s.ownerAddress)}</dd>
          <dt className="text-muted-foreground">Sponsor</dt>
          <dd>
            {s.sponsor ? (
              <>
                {s.sponsor.ownerFullName ?? s.sponsor.ownerEmail}{' '}
                <span className="text-muted-foreground font-mono text-xs">
                  ({s.sponsor.slotCode})
                </span>
              </>
            ) : (
              '(root)'
            )}
          </dd>
          {canSeeIncome && (
            <>
              <dt className="text-muted-foreground">Current wallet</dt>
              <dd className="tabular-nums">₱{Number(s.wallet?.available ?? 0).toFixed(2)}</dd>
              <dt className="text-muted-foreground">Total earned</dt>
              <dd className="tabular-nums">
                ₱{Number(s.wallet?.totalEarned ?? s.wallet?.lifetimeEarned ?? 0).toFixed(2)}
                {Number(s.wallet?.legacyEarnedOffset ?? 0) > 0 ? (
                  <span className="text-muted-foreground block text-xs">
                    ₱{Number(s.wallet?.lifetimeEarned ?? 0).toFixed(2)} in this system + ₱
                    {Number(s.wallet?.legacyEarnedOffset ?? 0).toFixed(2)} carried over (legacy
                    counter ₱{Number(s.wallet?.legacyTotalEarned ?? 0).toFixed(2)})
                  </span>
                ) : null}
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">Sponsor slot</dt>
          <dd>
            {s.sponsor ? (
              <Link to={`/slots/${s.sponsorSlotId}`} className="font-mono hover:underline">
                {s.sponsor.slotCode}
              </Link>
            ) : (
              '(root)'
            )}
          </dd>
          {/* The replicated link the OWNER signed up through, distinct from the
              genealogy sponsor above. Usually the same for a first slot, but an
              admin override, a sponsor typed at activation, a self-stacked 2nd
              slot, or a global link override all make them diverge — which is
              exactly when someone comes asking. */}
          <dt className="text-muted-foreground">Link sponsor</dt>
          <dd>
            {s.linkSponsor ? (
              <>
                {s.linkSponsor.ownerFullName ?? s.linkSponsor.ownerEmail}{' '}
                <span className="text-muted-foreground font-mono text-xs">
                  ({s.linkSponsor.slotCode})
                </span>
                {s.linkSponsor.status !== 'active' && (
                  <Badge variant="outline" className="ml-1.5 text-[10px]">
                    {s.linkSponsor.status}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </dd>
          <dt className="text-muted-foreground">Binary parent</dt>
          <dd>
            {!s.binary ? (
              // No binary_tree row at all → the slot is UNPLACED (sponsor-only,
              // not in the binary tree). Distinct from a true root, which HAS a
              // binary node with a null parent.
              <Badge variant="outline" className="text-[10px]">
                Unplaced
              </Badge>
            ) : s.binary.parentSlotId ? (
              <>
                <Link to={`/slots/${s.binary.parentSlotId}`} className="font-mono hover:underline">
                  {s.binary.parentSlotId.slice(0, 8)}…
                </Link>
                {s.binary.position && (
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {s.binary.position}
                  </Badge>
                )}
              </>
            ) : (
              '(root)'
            )}
          </dd>
          <dt className="text-muted-foreground">Activated at</dt>
          <dd>{s.activatedAt ? new Date(s.activatedAt).toLocaleString() : ''}</dd>
          <dt className="text-muted-foreground">Created at</dt>
          <dd>{new Date(s.createdAt).toLocaleString()}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

/**
 * CD/Half-CD debt panel. Rendered only for admins with the balance-adjust
 * permission; hidden entirely for slots that never carried a debt. A settled
 * debt stays visible (remaining 0, settled date) as the historical record.
 */
function CdDebtCard({ slotId }: { slotId: string }) {
  const qc = useQueryClient();
  const debtQ = useQuery({
    queryKey: ['slot-debt', slotId],
    queryFn: () => getSlotDebt(slotId),
  });
  const [op, setOp] = useState<'increase' | 'reduce' | 'forgive'>('reduce');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      adjustSlotDebt(slotId, { op, amount: op === 'forgive' ? undefined : amount.trim(), note }),
    onSuccess: () => {
      setAmount('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['slot-debt', slotId] });
      void qc.invalidateQueries({ queryKey: ['admin-slot', slotId] });
    },
  });

  const d = debtQ.data;
  if (!d) return null;

  const total = Number(d.debtTotal);
  const paid = Number(d.debtPaid);
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 100;
  const settled = !!d.settledAt;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {d.entrySlotType === 'half_cd' ? 'Half-CD' : 'CD'} Debt
          {settled ? (
            <Badge variant="default">Settled</Badge>
          ) : (
            <Badge variant="secondary">In debt</Badge>
          )}
          {d.entryCompFired && (
            <Badge
              variant="outline"
              title="Activation comp already paid to the uplines at redemption — settlement fires nothing extra"
            >
              entry comp paid upfront
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Total debt</p>
            <p className="tabular-nums font-medium">
              <Money amount={d.debtTotal} direction="neutral" />
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Paid</p>
            <p className="tabular-nums font-medium">
              <Money amount={d.debtPaid} direction="neutral" />
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">{settled ? 'Settled on' : 'Remaining'}</p>
            <p className="tabular-nums font-medium">
              {settled ? (
                new Date(d.settledAt!).toLocaleString()
              ) : (
                <Money amount={d.debtRemaining} direction="neutral" />
              )}
            </p>
          </div>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${settled ? 100 : pct}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Cash earnings pay this debt before reaching the wallet; the slot becomes a Paid slot when
          it settles. Adjustments here are audited and move no wallet money.
        </p>
        {!settled && (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Action</p>
              <select
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={op}
                onChange={(e) => setOp(e.target.value as typeof op)}
              >
                <option value="reduce">Reduce remaining</option>
                <option value="increase">Increase debt</option>
                <option value="forgive">Forgive (settle now)</option>
              </select>
            </div>
            {op !== 'forgive' && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Amount</p>
                <Input
                  className="h-9 w-32"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            )}
            <div className="flex-1 min-w-40">
              <p className="text-xs text-muted-foreground mb-1">Note</p>
              <Input
                className="h-9"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reason (kept in the audit log)"
              />
            </div>
            <Button
              size="sm"
              disabled={
                mutation.isPending || (op !== 'forgive' && (!amount.trim() || Number(amount) <= 0))
              }
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Apply
            </Button>
          </div>
        )}
        {mutation.isError && (
          <p className="text-xs text-destructive">
            {(mutation.error as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ?? 'Adjustment failed.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function WalletTab({ s }: { s: AdminSlotDetail }) {
  const { kindLabels, coinLabel } = useLedgerLabels();
  const w = s.wallet;
  const hasCoin = w != null && (Number(w.availableCoin) > 0 || Number(w.lifetimeCoinEarned) > 0);
  const hasToken = w != null && (Number(w.availableToken) > 0 || Number(w.lifetimeTokenEarned) > 0);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Wallet (this slot)</CardTitle>
        </CardHeader>
        <CardContent>
          {!w ? (
            <p className="text-sm text-muted-foreground">No wallet activity yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <Stat label="Available" value={w.available} />
              <Stat label="Pending" value={w.pending} />
              <Stat label="Lifetime earned" value={w.lifetimeEarned} />
              <Stat label="GC available" value={w.availableGc} />
              <Stat label="GC lifetime" value={w.lifetimeGcEarned} />
              {hasCoin ? (
                <>
                  <Stat label={`${coinLabel} available`} value={w.availableCoin} />
                  <Stat label={`${coinLabel} lifetime`} value={w.lifetimeCoinEarned} />
                </>
              ) : null}
              {hasToken ? (
                <>
                  <Stat label="Tokens available" value={w.availableToken} />
                  <Stat label="Tokens lifetime" value={w.lifetimeTokenEarned} />
                </>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Earnings breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!s.breakdown.length ? (
            <p className="p-6 text-sm text-muted-foreground">No earnings yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Currency</th>
                  <th className="px-4 py-2 font-medium text-right">Credits</th>
                  <th className="px-4 py-2 font-medium text-right">Debits</th>
                  <th className="px-4 py-2 font-medium text-right">Entries</th>
                  <th className="px-4 py-2 font-medium text-right">Last</th>
                </tr>
              </thead>
              <tbody>
                {s.breakdown.map((b) => (
                  <tr key={`${b.kind}:${b.currencyKind}`} className="border-b last:border-0">
                    <td className="px-4 py-2 text-xs" title={b.kind}>
                      {labelizeLedgerKind(b.kind, kindLabels)}
                    </td>
                    <td className="px-4 py-2 text-xs uppercase">
                      {currencyWord(b.currencyKind, coinLabel)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Money amount={b.totalCredit} direction="credit" showSign={false} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Money amount={b.totalDebit} direction="debit" showSign={false} />
                    </td>
                    <td className="px-4 py-2 text-right">{b.entryCount}</td>
                    <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                      {b.lastAt ? new Date(b.lastAt).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LedgerTab({ rows, loading }: { rows: LedgerRow[] | undefined; loading: boolean }) {
  const { kindLabels, planLabels, coinLabel } = useLedgerLabels();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Wallet logs</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : !rows?.length ? (
          <p className="p-6 text-sm text-muted-foreground">No entries.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">Lvl</th>
                <th className="px-4 py-2 font-medium">Triggered by</th>
                <th className="px-4 py-2 font-medium">Currency</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2" title={r.kind}>
                    {labelizeLedgerEntry(r.kind, r.metadata, kindLabels)}
                  </td>
                  <td className="px-4 py-2 text-xs" title={r.sourcePlan}>
                    {labelizeSourcePlan(r.sourcePlan, planLabels)}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.level ?? ''}</td>
                  <td className="px-4 py-2 text-xs">
                    {!r.fromSlotId && r.orderCode ? (
                      // Dropshipping income (and other order-sourced entries with
                      // no triggering slot): show the buyer + Trans#, not "none".
                      <div className="flex flex-col gap-0.5">
                        <span>{r.buyerName ?? 'Customer'}</span>
                        <span className="font-mono text-[11px] text-muted-foreground">
                          #{r.orderCode}
                        </span>
                      </div>
                    ) : (
                      // Prefer the plan's precise `from_slot_id`; fall back to the
                      // universal `trigger_slot_id` so plans that don't stamp a
                      // scalar from-slot (binary pairing, Mentor's Bonus) still
                      // show their cause slot instead of "none".
                      <TriggerSlotCell
                        slotId={r.fromSlotId ?? r.triggerSlotId}
                        slotCode={r.fromSlotCode ?? r.triggerSlotCode}
                        ownerName={r.fromOwnerName ?? r.triggerOwnerName}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs uppercase">
                    {currencyWord(r.currencyKind, coinLabel)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Money amount={r.amount} direction={r.direction === 'C' ? 'credit' : 'debit'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

const POINT_TYPE_LABEL: Record<string, { label: string; cls: string }> = {
  ppv: { label: 'PPV', cls: 'bg-emerald-100 text-emerald-800' },
  gpv: { label: 'GPV', cls: 'bg-teal-100 text-teal-800' },
  binary_l: { label: 'Binary L', cls: 'bg-sky-100 text-sky-800' },
  binary_r: { label: 'Binary R', cls: 'bg-indigo-100 text-indigo-800' },
  vortex_token: { label: 'Vortex', cls: 'bg-violet-100 text-violet-800' },
};

/**
 * Unified Points History — a time-ordered feed of point INFLOWS across plans:
 * PPV / GPV (from pv_history), Binary Left / Right contributions, and Vortex
 * token grants/spends. Binary mechanics (pairs, flushout, the full point log)
 * live in the Binary History tab.
 */
function PointsHistoryTab({ slotId }: { slotId: string }) {
  const q = useQuery({
    queryKey: ['admin-slot', slotId],
    queryFn: () => adminGetSlot(slotId),
  });
  const { kindLabels } = useLedgerLabels();
  const s = q.data;
  if (q.isLoading || !s) {
    return <Skeleton className="h-64" />;
  }
  return (
    <div className="space-y-4">
      {s.vortexActive && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vortex tokens</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Tokens earned toward Vortex queue entry, net of tokens spent on entry.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {Number(s.vortexTokenBalance).toFixed(2)}
            </div>
            <div className="text-xs text-muted-foreground">current token balance</div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Points history</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Every point inflow into this slot, newest first — personal/group PV, binary leg
            contributions, and Vortex tokens.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!s.pointsHistory.length ? (
            <p className="p-6 text-sm text-muted-foreground">No points recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                  <th className="px-4 py-2 font-medium text-right">Before</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                  <th className="px-4 py-2 font-medium text-right">After</th>
                </tr>
              </thead>
              <tbody>
                {s.pointsHistory.map((p, i) => {
                  const meta = POINT_TYPE_LABEL[p.type] ?? { label: p.type, cls: 'bg-muted' };
                  const amt = Number(p.amount);
                  const isOut = p.direction === 'out';
                  return (
                    <tr
                      key={`${p.sourceEventId}-${p.type}-${i}`}
                      className="border-b last:border-0"
                    >
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {new Date(p.occurredAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {p.type === 'vortex_token' ? (
                          <span title={p.kind}>{labelizeLedgerKind(p.kind, kindLabels)}</span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            {p.fromSlotCode ? (
                              <TriggerSlotCell
                                slotId={p.fromSlotId}
                                slotCode={p.fromSlotCode}
                                ownerName={null}
                              />
                            ) : (
                              <span className="text-muted-foreground">
                                {p.isDropship ? 'Dropship sale' : 'Own order'}
                              </span>
                            )}
                            {p.orderCode && (
                              <span className="font-mono text-[11px] text-muted-foreground">
                                #{p.orderCode}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {p.balanceBefore == null ? '—' : Number(p.balanceBefore).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Money
                          amount={amt.toFixed(2)}
                          direction={isOut ? 'debit' : 'credit'}
                          showSign={!isOut}
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {p.balanceAfter == null ? '—' : Number(p.balanceAfter).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal & Group PV by period</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Period totals. PPV = own orders; GPV = own + descendants.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!s.pvByPeriod.length ? (
            <p className="p-6 text-sm text-muted-foreground">No PV recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Range</th>
                  <th className="px-4 py-2 font-medium text-right">PPV</th>
                  <th className="px-4 py-2 font-medium text-right">GPV</th>
                  <th className="px-4 py-2 font-medium text-right">Legs</th>
                </tr>
              </thead>
              <tbody>
                {s.pvByPeriod.map((p) => (
                  <tr key={p.periodId} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{p.periodCode}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(p.periodStartAt).toLocaleDateString()} –{' '}
                      {new Date(p.periodEndAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-mono">
                      {Number(p.personalPv).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-mono">
                      {Number(p.groupPv).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right">{p.qualifyingLegs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Binary History — the binary-specific mechanics that used to clutter Points
 * History: cumulative leg PV, daily pairs, and the full leg-PV mutation log
 * (incl. pair_consumed / flushout). Placement audit lands here later.
 */
function BinaryHistoryTab({ slotId }: { slotId: string }) {
  const q = useQuery({
    queryKey: ['admin-slot', slotId],
    queryFn: () => adminGetSlot(slotId),
  });
  const { kindLabels } = useLedgerLabels();
  const s = q.data;
  if (q.isLoading || !s) {
    return <Skeleton className="h-64" />;
  }
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Binary points (cumulative)</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Lifetime PV accumulated on each leg from descendants' orders.
          </p>
        </CardHeader>
        <CardContent>
          {s.binary ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Left leg PV</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {Number(s.binary.leftPv).toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {s.binary.leftCount} slot{s.binary.leftCount === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Right leg PV</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {Number(s.binary.rightPv).toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {s.binary.rightCount} slot{s.binary.rightCount === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Not placed in the binary tree.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Binary pairs (daily, last 30 days)</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Pair-cycles credited per day. Stops when the daily cap hits.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!s.binaryDays.length ? (
            <p className="p-6 text-sm text-muted-foreground">No pairing activity yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium text-right">Pairs</th>
                  <th className="px-4 py-2 font-medium text-right">Pairing amount</th>
                  <th className="px-4 py-2 font-medium text-right">Flushed (cap)</th>
                  <th className="px-4 py-2 font-medium">Capped</th>
                </tr>
              </thead>
              <tbody>
                {s.binaryDays.map((d) => (
                  <tr key={d.date} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{d.date}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{d.pairsCount}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-mono">
                      {Number(d.earned).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                      {Number(d.flushed ?? 0) > 0 ? Number(d.flushed).toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {d.cappedAt ? new Date(d.cappedAt).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Binary history</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            One row per placement event: leg PV before / added / after on each side, the pairing
            cash &amp; GC paid, and any flushed income (daily cash cap or max-pairs-per-day count
            cap).
          </p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {!s.binaryHistory.length ? (
            <p className="p-6 text-sm text-muted-foreground">No binary activity yet.</p>
          ) : (
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-3 py-2 font-medium">Log Date</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Cause ID</th>
                  <th className="px-3 py-2 font-medium text-right">Before (Left)</th>
                  <th className="px-3 py-2 font-medium text-right">Before (Right)</th>
                  <th className="px-3 py-2 font-medium text-right">Add (Left)</th>
                  <th className="px-3 py-2 font-medium text-right">Add (Right)</th>
                  <th className="px-3 py-2 font-medium text-right">After (Left)</th>
                  <th className="px-3 py-2 font-medium text-right">After (Right)</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                  <th className="px-3 py-2 font-medium text-right">GC</th>
                  <th className="px-3 py-2 font-medium">Flushout</th>
                  <th className="px-3 py-2 font-medium text-right">Flushout Income</th>
                  <th className="px-3 py-2 font-medium">Slot</th>
                </tr>
              </thead>
              <tbody>
                {s.binaryHistory.map((h) => {
                  const num = (v: string | null) => (v == null ? '—' : Number(v).toFixed(2));
                  const typeLabel = h.kinds.length
                    ? h.kinds.map((k) => labelizeLedgerKind(k, kindLabels)).join(', ')
                    : '—';
                  return (
                    <tr key={h.sourceEventId} className="border-b last:border-0">
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(h.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-xs" title={h.kinds.join(', ')}>
                        {typeLabel}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <TriggerSlotCell
                          slotId={h.causeSlotId}
                          slotCode={h.causeSlotCode}
                          ownerName={h.causeOwnerName}
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.beforeLeft)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.beforeRight)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {num(h.addLeft)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {num(h.addRight)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.afterLeft)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.afterRight)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money
                          amount={Number(h.amount).toFixed(2)}
                          direction="credit"
                          showSign={false}
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {Number(h.gc).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {h.flushout ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            Yes
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {Number(h.flushoutIncome) > 0 ? Number(h.flushoutIncome).toFixed(2) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{s.slotCode}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type DistributionRun = {
  eventId: string;
  eventType: string;
  planCode: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  entries: Array<{
    slotId: string;
    slotCode: string | null;
    userId: string;
    ownerName: string | null;
    kind: string;
    level: number | null;
    amount: string;
    currencyKind: string;
  }>;
};

function DistributedTab({ slotId }: { slotId: string }) {
  const { kindLabels } = useLedgerLabels();
  const q = useQuery({
    queryKey: ['admin-slot-distributions', slotId],
    queryFn: async () =>
      (
        await api.get<{ rows: DistributionRun[] }>('/admin/distributions', {
          params: { triggerSlotId: slotId, limit: 100 },
        })
      ).data.rows,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Distributed income</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Comp plans that fired because of events from this slot, and the ledger rows each plan
          produced.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : !q.data?.length ? (
          <p className="p-6 text-sm text-muted-foreground">
            No distributions triggered by this slot yet.
          </p>
        ) : (
          <ul className="divide-y">
            {q.data.map((r) => (
              <li key={r.planCode + r.eventId} className="px-4 py-3 space-y-2">
                <div className="flex items-center gap-3 flex-wrap text-sm">
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted">
                    {r.planCode}
                  </span>
                  <span className="text-xs text-muted-foreground">{r.eventType}</span>
                  <Badge variant={r.status === 'succeeded' ? 'default' : 'destructive'}>
                    {r.status}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {r.startedAt ? new Date(r.startedAt).toLocaleString() : ''}
                  </span>
                </div>
                {r.error && <p className="text-xs text-destructive font-mono">{r.error}</p>}
                {r.entries.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No ledger entries (plan ran but emitted nothing).
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="text-left text-muted-foreground">
                      <tr>
                        <th className="py-1 font-medium">Recipient slot</th>
                        <th className="py-1 font-medium">Kind</th>
                        <th className="py-1 font-medium">Level</th>
                        <th className="py-1 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.entries.map((e, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-1">
                            <TriggerSlotCell
                              slotId={e.slotId}
                              slotCode={e.slotCode}
                              ownerName={e.ownerName}
                            />
                          </td>
                          <td className="py-1" title={e.kind}>
                            {labelizeLedgerKind(e.kind, kindLabels)}
                          </td>
                          <td className="py-1">{e.level ?? ''}</td>
                          <td className="py-1 text-right font-mono">
                            {Number(e.amount).toFixed(2)}{' '}
                            <span className="text-muted-foreground">
                              {e.currencyKind === 'gc' ? 'GC' : ''}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function GenealogyTab({ slotId, view }: { slotId: string; view: 'unilevel' | 'binary' }) {
  const qc = useQueryClient();
  // Lazy depth: start with root + its immediate level; expanding the lowest
  // loaded node fetches the next level (see onLoadChildren).
  const [depth, setDepth] = useState(1);
  const [addCtx, setAddCtx] = useState<AddSlotContext | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [target, setTarget] = useState<{ slotId: string; path: string[] } | null>(null);

  const q = useQuery({
    queryKey: ['admin-slot-genealogy', slotId, view, depth],
    queryFn: () => adminGetGenealogy({ slotId, view, depth }),
    // Keep the current tree visible while a deeper level loads (depth is in the
    // key) — otherwise each caret-expand blanks the tree to a skeleton and snaps
    // back, which reads as jumpiness.
    placeholderData: keepPreviousData,
  });

  const trimmed = searchTerm.trim();
  const searchQ = useQuery({
    queryKey: ['admin-genealogy-search', slotId, view, trimmed],
    queryFn: () => adminSearchGenealogy({ slotId, view, q: trimmed }),
    enabled: trimmed.length >= 2,
  });

  // Which per-box fields the binary tree shows (tenant admin setting).
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  function jumpTo(m: AdminGenealogySearchMatch) {
    setDepth((d) => clampDepth(Math.max(d, m.depth)));
    setTarget({ slotId: m.slotId, path: m.pathSlotIds });
    setSearchTerm('');
  }

  const rootCard: GenealogyTreeNode = q.data?.nodes.find((n) => n.slotId === slotId) ?? {
    slotId,
    slotCode: '…',
    userFullName: null,
    userEmail: null,
    depth: 0,
    parentSlotId: null,
    membershipCode: null,
    membershipName: null,
    packageAmount: null,
    descendantCount: 0,
    childCount: 0,
  };

  const nodes: GenealogyTreeNode[] = q.data?.nodes ?? [rootCard];
  const currency = q.data?.currency ?? 'PHP';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base capitalize">
          <span>{view} tree</span>
          {q.isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative mb-3 max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8 pr-8"
            placeholder="Find in this subtree (name, slot code, or referral code)…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {trimmed.length >= 2 && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
              {searchQ.isLoading ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
              ) : !searchQ.data?.matches.length ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">No match in this subtree.</p>
              ) : (
                <ul className="max-h-64 overflow-auto">
                  {searchQ.data.matches.map((m) => (
                    <li key={m.slotId}>
                      <button
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => jumpTo(m)}
                      >
                        <span className="min-w-0">
                          <span className="font-mono text-xs">{m.slotCode}</span>
                          {m.userFullName && (
                            <span className="ml-2 truncate text-muted-foreground">
                              {m.userFullName}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          depth {m.depth}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        {q.isLoading ? (
          <GenealogyTreeSkeleton />
        ) : (
          <GenealogyTree
            nodes={nodes}
            rootSlotId={slotId}
            view={view}
            currency={currency}
            onAddSlot={setAddCtx}
            nodeFields={
              view === 'binary' ? featuresQ.data?.binary_genealogy_node_fields : undefined
            }
            expandPath={target?.path}
            highlightSlotId={target?.slotId}
            defaultExpandedDepth={1}
            onLoadChildren={(n) => setDepth((d) => clampDepth(Math.max(d, n.depth + 1)))}
            emptyMessage="No downline."
          />
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Drag to pan, scroll+Ctrl (or pinch) to zoom; expand a node to load the next level.
        </p>
      </CardContent>

      {view === 'binary' && addCtx && addCtx.position ? (
        <PlaceSlotDialog
          open
          onOpenChange={(o) => {
            if (!o) setAddCtx(null);
          }}
          rootSlotId={slotId}
          parentSlotId={addCtx.parentSlotId}
          parentSlotCode={addCtx.parentSlotCode}
          position={addCtx.position}
          onSuccess={() => {
            // Deepen the fetch so the just-placed slot (one level below its
            // parent) is included in the refetch. Expanding a binary LEAF to
            // reveal its L/R placeholders doesn't fire onLoadChildren (guarded
            // by childCount > 0), so `depth` can be too shallow to ever load
            // the new node without a manual collapse/expand. +2 also lets the
            // new node show its own expand control without another round-trip.
            const parent = nodes.find((n) => n.slotId === addCtx.parentSlotId);
            if (parent) setDepth((d) => clampDepth(Math.max(d, parent.depth + 2)));
            refreshAfterPlacement(qc);
          }}
        />
      ) : (
        <CreateSlotDialog
          open={view === 'unilevel' && addCtx !== null}
          onOpenChange={(o) => {
            if (!o) setAddCtx(null);
          }}
          defaults={
            addCtx
              ? {
                  sponsorCode: addCtx.parentSlotCode,
                  sponsorLocked: true,
                }
              : undefined
          }
          onSuccess={() => refreshAfterPlacement(qc)}
        />
      )}
    </Card>
  );
}

function EmptyTab({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="p-8 text-center text-sm text-muted-foreground">{message}</CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">
        <Money amount={value} direction="neutral" />
      </div>
    </div>
  );
}

/** Compact one-line render of the owner's structured address (jsonb). */
function formatAddress(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '—';
  const parts = ['line1', 'line2', 'barangay', 'city', 'province', 'region', 'postalCode']
    .map((k) => addr[k])
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  return parts.length ? parts.join(', ') : '—';
}

/**
 * Reassign a slot to a new owner. The slot's wallet balance + lifetime
 * earnings stay with the slot — only the owner pointer moves. Audited
 * server-side.
 */
function CompanyToggle({
  slotId,
  isCompany,
  qc,
}: {
  slotId: string;
  isCompany: boolean;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const mut = useMutation({
    mutationFn: () => api.post(`/admin/slots/${slotId}/company`, { isCompany: !isCompany }),
    meta: { successMessage: 'Company flag updated.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-slot', slotId] });
      qc.invalidateQueries({ queryKey: ['admin-slots'] });
    },
  });
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
      title="Company slots are exempt from the Unilevel/Stairstep personal-PV maintenance gate."
    >
      {mut.isPending ? 'Saving…' : isCompany ? 'Unmark company' : 'Mark as company'}
    </Button>
  );
}

function ChangeOwnerDialog({
  slotId,
  currentOwnerLabel,
}: {
  slotId: string;
  currentOwnerLabel: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<AdminUserSearchResult | null>(null);

  const mut = useMutation({
    mutationFn: () => adminChangeSlotOwner(slotId, picked!.id),
    meta: { successMessage: 'Owner changed.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-slot', slotId] });
      qc.invalidateQueries({ queryKey: ['admin-slots'] });
      setOpen(false);
      setPicked(null);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Change owner
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change slot owner</DialogTitle>
          <DialogDescription>
            Reassign this slot from <span className="font-medium">{currentOwnerLabel}</span> to
            another active member. The slot&rsquo;s wallet balance and lifetime earnings stay with
            the slot.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">New owner</label>
          <MemberPicker selected={picked} onChange={setPicked} />
        </div>
        {mut.isError && (
          <p className="text-sm text-destructive">
            {(mut.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'Failed to change owner.'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!picked || mut.isPending}>
            {mut.isPending ? 'Reassigning…' : 'Reassign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Move a slot (and its sponsor-tree descendants) under a different sponsor, and
 * re-attribute the commission already paid up the old chain.
 *
 * Two things make this dialog more than a picker. It PREVIEWS first — the exact
 * credits that will be reversed and re-issued are listed before anything is
 * submitted, because the operator is signing off on other people's money moving
 * between wallets. And it refuses outright once the slot is in the binary tree:
 * a binary seat must sit inside its own sponsor's subtree, so re-rooting a
 * placed slot would strand it outside its new sponsor's leg. The server enforces
 * both; this mirrors them so the reason is visible before the click.
 */
function TransferSponsorDialog({
  slotId,
  slotCode,
  currentSponsorCode,
  isPlaced,
}: {
  slotId: string;
  slotCode: string;
  currentSponsorCode: string | null;
  isPlaced: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [moveComp, setMoveComp] = useState(true);
  const [reason, setReason] = useState('');

  const previewQ = useQuery<SponsorTransferPreview>({
    queryKey: ['sponsor-transfer-preview', slotId, code],
    queryFn: () => adminPreviewSponsorTransfer(slotId, code!),
    enabled: open && !!code,
    retry: false,
  });

  const mut = useMutation({
    mutationFn: () => adminTransferSponsor(slotId, { newSponsorCode: code!, moveComp, reason }),
    meta: { successMessage: 'Sponsor transferred.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-slot', slotId] });
      qc.invalidateQueries({ queryKey: ['admin-slots'] });
      setOpen(false);
      setCode(null);
      setReason('');
    },
  });

  const preview = previewQ.data;
  const blockers = preview?.blockers ?? [];
  const canSubmit = !!code && !!preview && blockers.length === 0 && reason.trim().length > 0;

  const reset = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setCode(null);
      setReason('');
      mut.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={isPlaced}>
        Transfer sponsor
      </Button>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Transfer sponsor</DialogTitle>
          <DialogDescription>
            Move <span className="font-medium">{slotCode}</span>
            {currentSponsorCode ? (
              <>
                {' '}
                from <span className="font-medium">{currentSponsorCode}</span>
              </>
            ) : null}{' '}
            to another sponsor. Everything beneath it in the unilevel tree moves with it.
          </DialogDescription>
        </DialogHeader>

        {isPlaced ? (
          <p className="text-sm text-destructive">
            This slot is already placed in the binary tree, so its sponsor cannot be moved. A binary
            seat has to sit inside its own sponsor&rsquo;s leg — re-rooting it would strand it
            outside the new sponsor&rsquo;s subtree.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">New sponsor slot</label>
              <SlotCodePicker value={code} onChange={setCode} />
            </div>

            {previewQ.isFetching && (
              <p className="text-sm text-muted-foreground">Checking what would move…</p>
            )}
            {previewQ.isError && (
              <p className="text-sm text-destructive">
                {(previewQ.error as { response?: { data?: { error?: string } } })?.response?.data
                  ?.error ?? 'Could not preview this transfer.'}
              </p>
            )}

            {preview && (
              <div className="space-y-3 rounded-md border p-3 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  <span>
                    <span className="text-muted-foreground">New sponsor: </span>
                    <span className="font-medium">{preview.newSponsorSlotCode}</span>
                    {preview.newSponsorName ? ` — ${preview.newSponsorName}` : ''}
                  </span>
                  <span>
                    <span className="text-muted-foreground">Slots moving: </span>
                    <span className="font-medium">{preview.subtreeSize}</span>
                  </span>
                </div>

                {preview.alreadyTransferred && (
                  <p className="text-muted-foreground">
                    This slot already sits under that sponsor. Submitting will only finish any
                    commission replay left pending from an earlier attempt.
                  </p>
                )}

                {blockers.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-4 text-destructive">
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : preview.affectedCredits.length === 0 ? (
                  <p className="text-muted-foreground">
                    No commission has been paid from this slot yet — only the tree moves.
                  </p>
                ) : (
                  <div className="space-y-1">
                    <p className="text-muted-foreground">
                      {preview.affectedCredits.length} credit
                      {preview.affectedCredits.length === 1 ? '' : 's'} will be reversed and
                      re-issued against the corrected upline. Amounts are recalculated from the new
                      sponsor&rsquo;s own tier, so they may differ from the figures below.
                    </p>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr className="text-left">
                            <th className="py-1 pr-2 font-normal">Paid to</th>
                            <th className="py-1 pr-2 font-normal">Bonus</th>
                            <th className="py-1 pr-2 text-right font-normal">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.affectedCredits.map((c) => (
                            <tr key={c.ledgerId} className="border-t">
                              <td className="py-1 pr-2">
                                {c.recipientSlotCode}
                                {c.recipientName ? ` — ${c.recipientName}` : ''}
                              </td>
                              <td className="py-1 pr-2">
                                {c.kind}
                                {c.level !== null ? ` L${c.level}` : ''}
                              </td>
                              <td className="py-1 pr-2 text-right">
                                <Money amount={c.amount} direction="neutral" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={moveComp}
                onCheckedChange={(v) => setMoveComp(v === true)}
                className="mt-0.5"
              />
              <span>
                Also move the commission
                <span className="block text-xs text-muted-foreground">
                  Leave this on unless the money is being settled some other way. Turning it off
                  fixes the tree and leaves every peso on the old upline.
                </span>
              </span>
            </label>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                Reason (shown to the members whose wallets change)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Sponsor corrected to the member's registered sponsor."
              />
            </div>
          </div>
        )}

        {mut.isError && (
          <p className="text-sm text-destructive">
            {(mut.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'Failed to transfer sponsor.'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!canSubmit || mut.isPending}>
            {mut.isPending ? 'Transferring…' : 'Transfer sponsor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SUPER_ADMIN ONLY. Upgrade a slot to a strictly-higher membership tier without
 * a kit code — no purchase is recorded. A free->paid upgrade fires live
 * activation + placement commissions to the uplines (the slot earns for the
 * first time); paid->paid fires nothing unless the tenant enables
 * `paid_upgrade_refires_bonuses`. Server enforces both the super_admin gate and
 * the strictly-higher-hierarchy rule; this dialog mirrors them for UX.
 */
function UpgradeSlotDialog({
  s,
  qc,
}: {
  s: AdminSlotDetail;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [open, setOpen] = useState(false);
  const inDebt = s.slotType === 'cd' || s.slotType === 'half_cd';
  const currentType: 'paid' | 'free' = s.slotType === 'free' ? 'free' : 'paid';

  const membershipsQ = useQuery({
    queryKey: ['memberships'],
    queryFn: listMemberships,
    enabled: open,
  });
  const memberships = membershipsQ.data ?? [];
  const current = memberships.find((m) => m.id === s.membershipId);
  const currentHierarchy = current ? membershipHierarchy(current.config) : null;
  const higherTiers =
    currentHierarchy === null
      ? []
      : sortMembershipsByHierarchy(
          memberships.filter((m) => {
            const h = membershipHierarchy(m.config);
            return h !== null && h > currentHierarchy;
          }),
        );

  const [targetCode, setTargetCode] = useState('');
  // Default the target type to the slot's current type: paid->paid / free->free
  // are the no-surprise defaults. An admin bumps free->paid explicitly (which
  // fires activation comp). paid->free is rejected server-side, so a paid slot
  // has no type choice.
  const [targetType, setTargetType] = useState<'paid' | 'free'>(currentType);
  const willFireActivation = currentType === 'free' && targetType === 'paid';

  const mut = useMutation({
    mutationFn: () =>
      adminUpgradeSlot(s.id, { targetMembershipCode: targetCode, targetSlotType: targetType }),
    meta: { successMessage: 'Slot upgraded.' },
    onSuccess: () => {
      // free->paid fans out activation/placement comp asynchronously; refresh the
      // slot views now and again after the materializer drains.
      refreshAfterPlacement(qc);
      qc.invalidateQueries({ queryKey: ['admin-slots'] });
      setOpen(false);
      setTargetCode('');
    },
  });

  if (inDebt) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled
        title="This slot has an open CD/Half-CD debt. Settle the debt (Information tab) before upgrading its tier."
      >
        Upgrade tier
      </Button>
    );
  }

  const noHierarchy = !membershipsQ.isLoading && currentHierarchy === null;
  const noHigher = !membershipsQ.isLoading && currentHierarchy !== null && higherTiers.length === 0;
  const blocked = noHierarchy || noHigher;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Upgrade tier
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upgrade membership tier</DialogTitle>
          <DialogDescription>
            Move this slot to a higher membership tier with no kit code and no payment recorded. The
            target tier must rank strictly higher than the current one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div>
            <span className="text-muted-foreground">Current tier: </span>
            <span className="font-medium">{s.membershipName}</span>{' '}
            <span className="text-muted-foreground">({s.membershipCode})</span>
            <Badge variant="secondary" className="ml-2 capitalize">
              {currentType}
            </Badge>
          </div>

          {membershipsQ.isLoading ? (
            <p className="text-muted-foreground">Loading tiers…</p>
          ) : noHierarchy ? (
            <p className="text-destructive">
              This slot&rsquo;s current tier has no hierarchy rank configured, so
              &ldquo;higher&rdquo; is undefined. Set a tier rank on the Memberships page first.
            </p>
          ) : noHigher ? (
            <p className="text-destructive">
              No tier ranks above <span className="font-medium">{s.membershipName}</span>. Configure
              a higher tier&rsquo;s rank on the Memberships page first.
            </p>
          ) : (
            <>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Upgrade to</label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={targetCode}
                  onChange={(e) => setTargetCode(e.target.value)}
                >
                  <option value="">Select a higher tier…</option>
                  {higherTiers.map((m) => (
                    <option key={m.id} value={m.code}>
                      {m.name} ({m.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Slot type after upgrade
                </label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60"
                  value={targetType}
                  disabled={currentType === 'paid'}
                  onChange={(e) => setTargetType(e.target.value as 'paid' | 'free')}
                >
                  {currentType === 'free' && <option value="free">Free (non-earning)</option>}
                  <option value="paid">Paid (earning)</option>
                </select>
                {currentType === 'paid' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    A paid slot stays paid — downgrading to free isn&rsquo;t allowed.
                  </p>
                )}
              </div>

              {willFireActivation ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <span className="font-medium">Heads up:</span> this fires activation and placement
                  commissions to the uplines — the slot becomes a paying earner for the first time,
                  with no purchase behind it. This is not reversible.
                </div>
              ) : currentType === 'paid' ? (
                <p className="text-xs text-muted-foreground">
                  No commissions fire on a paid → paid upgrade unless{' '}
                  <span className="font-medium">Paid upgrade re-fires bonuses</span> is enabled in
                  Tenant Settings.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Stays a free (non-earning) slot; no commissions fire.
                </p>
              )}
            </>
          )}
        </div>

        {mut.isError && (
          <p className="text-sm text-destructive">
            {(mut.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'Upgrade failed.'}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={blocked || !targetCode || mut.isPending}>
            {mut.isPending ? 'Upgrading…' : 'Upgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const COMP_STATUS_META: Record<
  AdminUpgradeHistoryRow['compStatus'],
  { label: string; cls: string }
> = {
  never: { label: 'Not fired', cls: 'bg-muted text-muted-foreground' },
  fired: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-800' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  pending: { label: 'Pending', cls: 'bg-sky-100 text-sky-800' },
};

const REFIRE_OUTCOME: Record<string, { variant: 'success' | 'error' | 'info'; text: string }> = {
  enqueued: { variant: 'success', text: 'Bonus queued — the uplines will be paid shortly.' },
  already_fired: { variant: 'info', text: 'This upgrade bonus already fired — nothing to do.' },
  nothing_to_fire: { variant: 'info', text: 'This upgrade has no activation bonus (free tier).' },
  blocked_superseded: {
    variant: 'error',
    text: 'This slot has since been upgraded to a higher tier — re-firing would pay the wrong tier. Only the latest upgrade can be re-fired.',
  },
  blocked_cd_settlement: {
    variant: 'error',
    text: 'This is a CD/Half-CD settlement — its entry commissions are handled by the settlement sweep, not here.',
  },
  blocked_in_flight: {
    variant: 'error',
    text: 'A comp run for this upgrade is still in flight. Wait for it to finish before re-firing.',
  },
  blocked_failed: {
    variant: 'error',
    text: 'A prior comp attempt for this upgrade failed and cannot be auto-recovered safely. Have engineering clear the stale comp records before re-firing.',
  },
};

/**
 * SUPER_ADMIN. Lists a slot's upgrades and lets you re-fire an upgrade's
 * activation/placement bonus that never paid (e.g. a paid->paid upgrade done
 * while `paid_upgrade_refires_bonuses` was off). Re-firing reuses the upgrade's
 * deterministic comp event id, so it fires exactly once and never double-pays.
 * Only a genuinely-unfired ("Not fired") row is re-fireable — anything already
 * paid, in-flight, failed, or superseded is surfaced by the server as a blocked
 * outcome and left for manual handling.
 */
function UpgradeHistoryCard({
  slotId,
  qc,
}: {
  slotId: string;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const historyQ = useQuery({
    queryKey: ['admin-slot-upgrade-history', slotId],
    queryFn: () => adminGetUpgradeHistory(slotId),
  });

  const mut = useMutation({
    mutationFn: (id: string) => adminRefireUpgradeBonus(slotId, { upgradeHistoryId: id }),
    onSuccess: (result) => {
      const m = REFIRE_OUTCOME[result.outcome] ?? {
        variant: 'info' as const,
        text: result.outcome,
      };
      toast[m.variant](m.text);
      void qc.invalidateQueries({ queryKey: ['admin-slot-upgrade-history', slotId] });
      // A fired re-fire fans out comp asynchronously — refresh the slot views now
      // and again after the materializer drains.
      if (result.outcome === 'enqueued') refreshAfterPlacement(qc);
    },
  });

  const rows = historyQ.data ?? [];
  if (historyQ.isLoading || rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Upgrade history</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Each tier change and whether its activation bonus fired. Re-firing pays the uplines the
          activation + placement commissions for that upgrade — it reuses the upgrade&rsquo;s event
          id, so it fires once and never double-pays.
        </p>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="text-left text-muted-foreground border-b">
            <tr>
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Change</th>
              <th className="px-4 py-2 font-medium">Bonus</th>
              <th className="px-4 py-2 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const meta = COMP_STATUS_META[r.compStatus];
              const isPaidTarget = r.afterSlotType === 'paid';
              // Only a genuinely-unfired paid upgrade is re-fireable from the UI.
              // Every other state (fired/partial/failed/pending) is a server-side
              // block, so we don't invite the click.
              const canRefire = isPaidTarget && r.compStatus === 'never';
              return (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.beforeMembershipName}
                    <span className="text-muted-foreground"> ({r.beforeSlotType})</span>
                    <span className="text-muted-foreground"> → </span>
                    {r.afterMembershipName}
                    <span className="text-muted-foreground"> ({r.afterSlotType})</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!isPaidTarget ? (
                      <span className="text-xs text-muted-foreground">no bonus</span>
                    ) : canRefire ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mut.isPending}
                        onClick={() => mut.mutate(r.id)}
                      >
                        {mut.isPending ? 'Working…' : 'Re-fire bonus'}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
